<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
        <h4 class="mb-0">
          {{ $t('ledger_sub_bills') }}
        </h4>
        <b-button
          v-if="canPost"
          variant="primary"
          class="rounded-pill"
          @click="openCreate"
        >
          {{ $t('ledger_sub_bill_new') }}
        </b-button>
      </div>
      <b-row>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-select
            v-model="filter.status"
            :options="statusOptions"
            @change="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-checkbox
            v-model="filter.open"
            class="mt-50"
            @change="reload"
          >
            {{ $t('ledger_sub_open_only') }}
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-input
            v-model="filter.from"
            type="date"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-input
            v-model="filter.to"
            type="date"
          />
        </b-col>
        <b-col
          v-if="canCompany"
          cols="12"
          md="3"
        >
          <b-form-checkbox
            v-model="filter.company"
            @change="reload"
          >
            {{ $t('ledger_rep_company_toggle') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-muted mt-1"
      >
        {{ $t('ledger_sub_empty_bills') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block mt-1">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(bill_number)="data">
              <b-link :to="{ name: 'ledger-subcontract-bills-view', params: { id: data.item.id } }">
                {{ data.item.bill_number }}
              </b-link>
            </template>
            <template #cell(subcontractor)="data">
              {{ (data.item.subcontractor && data.item.subcontractor.name) || '—' }}
            </template>
            <template #cell(gross_amount)="data"><span class="ledger-num">{{ money(data.item.gross_amount) }}</span></template>
            <template #cell(retention_amount)="data"><span class="ledger-num">{{ money(data.item.retention_amount) }}</span></template>
            <template #cell(net_payable)="data"><span class="ledger-num">{{ money(data.item.net_payable) }}</span></template>
            <template #cell(balance_due)="data"><span class="ledger-num">{{ money(data.item.balance_due) }}</span></template>
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
            </template>
          </b-table>
        </div>
        <div class="d-md-none mt-1">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-sub-card"
            @click="$router.push({ name: 'ledger-subcontract-bills-view', params: { id: item.id } })"
          >
            <strong>{{ item.bill_number }}</strong>
            <div class="ledger-num">
              {{ money(item.balance_due) }}
            </div>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="filter.page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || 15"
          @change="onPage"
        />
      </div>
    </b-card>

    <b-modal
      v-model="createOpen"
      :title="$t('ledger_sub_bill_new')"
      hide-footer
    >
      <p class="text-muted small">
        {{ $t('ledger_sub_bill_hint', { code: billPartyCode }) }}
      </p>
      <b-form-group :label="$t('ledger_sub_title')">
        <v-select
          :value="subOption"
          :options="subOptions"
          label="label"
          :filterable="false"
          :dir="isRtl ? 'rtl' : 'ltr'"
          @search="searchSubs"
          @input="onSub"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_payroll_project')">
        <v-select
          :value="projectOption"
          :options="projectOptions"
          label="label"
          :filterable="false"
          :dir="isRtl ? 'rtl' : 'ltr'"
          @search="searchProjects"
          @input="onProject"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_sub_gross')">
        <b-form-input
          v-model.number="form.gross_amount"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_ct_retention_pct')">
        <b-form-input
          v-model.number="form.retention_percent"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <div
        v-if="preview"
        class="ledger-num mb-1"
      >
        <div>{{ $t('ledger_ct_retention') }} {{ money(preview.retention) }}</div>
        <strong>{{ $t('ledger_sub_net') }} {{ money(preview.net) }}</strong>
      </div>
      <b-form-group :label="$t('ledger_date')">
        <b-form-input
          v-model="form.bill_date"
          type="date"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_description')">
        <b-form-input v-model="form.description" />
      </b-form-group>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canCreate"
        @click="submit"
      >
        {{ $t('ledger_rec_post') }}
      </b-button>
    </b-modal>
    <div
      v-if="rows.length"
      class="ledger-sub-sticky d-md-none"
    >
      <span>{{ $t('ledger_inv_balance') }}</span>
      <strong class="ledger-num">{{ money(balanceSum) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormSelect, BFormCheckbox, BFormInput, BButton, BTable, BLink, BBadge, BSpinner, BPagination, BModal, BFormGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Projects from '@/api/projects/projects'
import { getSubcontractors, getSubcontractBills, createSubcontractBill } from '@/api/accounting/subcontractors'
import {
  listFrom, oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, todayIso, apiErrorMessage,
  glAccountCode,
} from '@/utils/ledger'

const projectsApi = new Projects()
const STATUS_VARIANT = { posted: 'info', partially_paid: 'warning', paid: 'success', cancelled: 'dark' }

export default {
  name: 'SubcontractBillList',
  components: {
    BCard, BRow, BCol, BFormSelect, BFormCheckbox, BFormInput, BButton, BTable, BLink, BBadge, BSpinner, BPagination, BModal, BFormGroup, vSelect,
  },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      meta: {},
      createOpen: false,
      subOptions: [],
      projectOptions: [],
      subOption: null,
      projectOption: null,
      filter: { status: '', open: false, from: '', to: '', company: false, page: 1 },
      form: { subcontractor_id: null, project_id: null, gross_amount: null, retention_percent: 0, bill_date: todayIso(), description: '' },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    canPost() {
      return this.hasPermission('post subcontractor_bills')
    },
    canCompany() {
      return this.hasPermission('view company_reports')
    },
    statusOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_statuses') },
        { value: 'posted', text: this.$t('ledger_sub_status_posted') },
        { value: 'partially_paid', text: this.$t('ledger_inv_status_partial') },
        { value: 'paid', text: this.$t('ledger_inv_status_paid') },
        { value: 'cancelled', text: this.$t('ledger_inv_status_cancelled') },
      ]
    },
    fields() {
      return [
        { key: 'bill_number', label: this.$t('ledger_inv_number') },
        { key: 'bill_date', label: this.$t('ledger_date') },
        { key: 'subcontractor', label: this.$t('ledger_sub_title') },
        { key: 'gross_amount', label: this.$t('ledger_sub_gross') },
        { key: 'retention_amount', label: this.$t('ledger_ct_retention') },
        { key: 'net_payable', label: this.$t('ledger_sub_net') },
        { key: 'balance_due', label: this.$t('ledger_inv_balance') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
    preview() {
      const gross = Number(this.form.gross_amount)
      if (!Number.isFinite(gross)) return null
      const pct = Number(this.form.retention_percent) || 0
      const retention = roundMoney(gross * pct / 100)
      return { retention, net: roundMoney(gross - retention) }
    },
    billPartyCode() {
      const raw = this.subOption && this.subOption.raw
      return glAccountCode(raw) || '2115-……'
    },
    canCreate() {
      const pct = Number(this.form.retention_percent) || 0
      return this.form.subcontractor_id && this.form.project_id && Number(this.form.gross_amount) > 0 && pct >= 0 && pct < 100
    },
    balanceSum() {
      return roundMoney(this.rows.reduce((sum, row) => sum + (Number(row.balance_due) || 0), 0))
    },
  },
  mounted() {
    if (this.$route.query.subcontractor_id) this.filter.subcontractor_id = this.$route.query.subcontractor_id
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    statusVariant(status) {
      return STATUS_VARIANT[enumValue(status)] || 'secondary'
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    openCreate() {
      this.form = { subcontractor_id: null, project_id: null, gross_amount: null, retention_percent: 0, bill_date: todayIso(), description: '' }
      this.subOption = null
      this.projectOption = null
      this.createOpen = true
    },
    searchSubs(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.subTimer)
      this.subTimer = setTimeout(async () => {
        try {
          const res = await getSubcontractors({ keyword: q, per_page: 20, is_active: 1 })
          this.subOptions = listFrom(res).map(row => ({ id: row.id, label: row.name, raw: row }))
        } catch (error) {
          this.subOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    searchProjects(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.projectTimer)
      this.projectTimer = setTimeout(async () => {
        try {
          const res = await projectsApi.listProjects({ search: q, per_page: 20, page: 1 })
          this.projectOptions = listFrom(res).map(project => ({
            id: project.id,
            label: `${project.project_number || project.id} — ${project.name || ''}`,
          }))
        } catch (error) {
          this.projectOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    onSub(option) {
      this.subOption = option
      this.form.subcontractor_id = option ? option.id : null
    },
    onProject(option) {
      this.projectOption = option
      this.form.project_id = option ? option.id : null
    },
    reload() {
      this.filter.page = 1
      this.load()
    },
    onPage(page) {
      this.filter.page = page
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { page: this.filter.page, per_page: 15 }
        if (this.filter.status) params.status = this.filter.status
        if (this.filter.open) params.open = 1
        if (this.filter.from) params.from = this.filter.from
        if (this.filter.to) params.to = this.filter.to
        if (this.filter.subcontractor_id) params.subcontractor_id = this.filter.subcontractor_id
        if (this.filter.company && this.canCompany) params.scope = 'company'
        const res = await getSubcontractBills(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async submit() {
      if (!this.canCreate || this.saving) return
      this.saving = true
      try {
        const res = await createSubcontractBill({
          subcontractor_id: this.form.subcontractor_id,
          project_id: this.form.project_id,
          gross_amount: roundMoney(this.form.gross_amount),
          retention_percent: Number(this.form.retention_percent) || 0,
          bill_date: this.form.bill_date || undefined,
          description: String(this.form.description || '').trim() || undefined,
        })
        const bill = oneFrom(res)
        this.createOpen = false
        this.toast(this.$t('ledger_sub_posted'), 'success')
        if (bill && bill.id) this.$router.push({ name: 'ledger-subcontract-bills-view', params: { id: bill.id } })
        else this.reload()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-sub-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
.ledger-sub-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
